import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Image,
  Heading,
  Text,
  Button,
  HStack,
  VStack,
  Badge,
  SimpleGrid,
  Stack,
  Divider,
  Flex,
  Tag,
  IconButton,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from "@chakra-ui/react";
import { EditIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import assetsService from "../../services/rentals/assetsService";

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mainImg, setMainImg] = useState(null);

  useEffect(() => {
    setLoading(true);
    assetsService
      .getAsset(id)
      .then((res) => setAsset(res))
      .catch(() => setAsset(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (asset) {
      const url =
        (asset.images && asset.images[0] && asset.images[0].url) ||
        "https://placehold.co/900x600?text=no+image";
      setMainImg(url);
    }
  }, [asset]);

  if (loading)
    return (
      <Box p={6}>
        <Text>Loading...</Text>
      </Box>
    );

  if (!asset)
    return (
      <Box p={6}>
        <Text>Asset not found</Text>
      </Box>
    );

  const placeholder = "https://source.unsplash.com/900x600/?car";
  const displayImg = mainImg || placeholder;

  return (
    <Box p={6}>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        {/* Left: images */}
        <Box>
          <Box
            borderRadius={8}
            overflow="hidden"
            boxShadow="md"
            position="relative"
          >
            <Image
              src={displayImg}
              alt={asset.asset_id}
              width="100%"
              maxH="520px"
              objectFit="cover"
            />
            <Box
              position="absolute"
              top={3}
              left={3}
              bg="blackAlpha.600"
              color="white"
              px={3}
              py={1}
              borderRadius="md"
            >
              <Text fontSize="sm" fontWeight={600}>
                {asset.asset_id}
              </Text>
            </Box>
          </Box>
          <HStack mt={3} spacing={2} overflowX="auto">
            {(asset.images || []).map((img, idx) => (
              <Box
                key={idx}
                borderWidth={1}
                borderColor={img.url === displayImg ? "blue.300" : "gray.100"}
                borderRadius={6}
                overflow="hidden"
                onClick={() => setMainImg(img.url)}
                _hover={{ cursor: "pointer", opacity: 0.9 }}
              >
                <Image
                  src={img.url}
                  alt={`img-${idx}`}
                  boxSize="80px"
                  objectFit="cover"
                />
              </Box>
            ))}
          </HStack>
        </Box>

        {/* Right: details */}
        <VStack align="start" spacing={4}>
          <HStack justify="space-between" width="100%">
            <Heading size="lg">{`${asset.make} ${asset.model} ${
              asset.year || ""
            }`}</Heading>
            <Badge
              colorScheme={
                asset.availability_status === "available" ? "green" : "orange"
              }
            >
              {asset.availability_status}
            </Badge>
          </HStack>

          <Text color="gray.600">
            {asset.vin || asset.license_plate || asset.asset_id}
          </Text>

          <HStack spacing={4} width="100%">
            <Text fontSize="13px" color="gray.500">
              Base:
            </Text>
            <Text fontWeight={600}>{asset.base_location || "-"}</Text>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                const coords =
                  asset.current_location && asset.current_location.coordinates;
                if (coords && coords.length >= 2) {
                  const [lon, lat] = coords;
                  window.open(
                    `https://www.google.com/maps?q=${lat},${lon}`,
                    "_blank"
                  );
                }
              }}
            >
              <HStack>
                <ExternalLinkIcon />
                <Text>Open in Maps</Text>
              </HStack>
            </Button>
          </HStack>

          <Divider />

          <Flex align="center" justify="space-between" width="100%">
            <Stat>
              <StatLabel>Price</StatLabel>
              <StatNumber>
                {asset.pricing && asset.pricing.daily
                  ? `$${asset.pricing.daily}`
                  : "-"}
              </StatNumber>
              <StatHelpText>/day</StatHelpText>
            </Stat>
            <HStack>
              <Tag size="sm">{asset.category}</Tag>
              <Tag size="sm">{asset.specs && asset.specs.transmission}</Tag>
              <Tag size="sm">{asset.specs && asset.specs.fuel_type}</Tag>
            </HStack>
          </Flex>

          <HStack spacing={6} width="100%">
            <Text fontSize="12px" color="gray.500">
              Created:
            </Text>
            <Text>
              {asset.created_at
                ? new Date(asset.created_at).toLocaleString()
                : "-"}
            </Text>
            <Text fontSize="12px" color="gray.500">
              Updated:
            </Text>
            <Text>
              {asset.updated_at
                ? new Date(asset.updated_at).toLocaleString()
                : "-"}
            </Text>
          </HStack>

          <SimpleGrid columns={2} gap={3} width="100%">
            <Box>
              <Text fontSize="12px" color="gray.500">
                Seats
              </Text>
              <Text fontWeight={600}>{asset.specs && asset.specs.seats}</Text>
            </Box>
            <Box>
              <Text fontSize="12px" color="gray.500">
                Transmission
              </Text>
              <Text fontWeight={600}>
                {asset.specs && asset.specs.transmission}
              </Text>
            </Box>
            <Box>
              <Text fontSize="12px" color="gray.500">
                Fuel
              </Text>
              <Text fontWeight={600}>
                {asset.specs && asset.specs.fuel_type}
              </Text>
            </Box>
            <Box>
              <Text fontSize="12px" color="gray.500">
                Odometer
              </Text>
              <Text fontWeight={600}>
                {typeof asset.odometer === "number"
                  ? asset.odometer.toLocaleString()
                  : "-"}
              </Text>
            </Box>
          </SimpleGrid>

          {asset.maintenance_records &&
            asset.maintenance_records.length > 0 && (
              <Box width="100%">
                <Heading size="sm">Maintenance</Heading>
                {asset.maintenance_records.map((m, i) => (
                  <Box key={i} p={2} borderWidth={1} borderRadius={6} mt={2}>
                    <HStack justify="space-between">
                      <Text>
                        {m.type} —{" "}
                        {m.performed_at
                          ? new Date(m.performed_at).toLocaleDateString()
                          : ""}
                      </Text>
                      <Text>${m.cost}</Text>
                    </HStack>
                    <Text fontSize="12px" color="gray.600">
                      {m.vendor} — {m.notes || ""}
                    </Text>
                  </Box>
                ))}
              </Box>
            )}

          <HStack pt={4} spacing={4}>
            <Button
              colorScheme="teal"
              onClick={() =>
                navigate(`/rentals/cart?assetId=${asset.asset_id || asset._id}`)
              }
            >
              Reserve
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  await assetsService.updateAsset(asset.asset_id || asset._id, {
                    availability_status: "available",
                  });
                  const updated = await assetsService.getAsset(
                    asset.asset_id || asset._id
                  );
                  setAsset(updated);
                } catch (e) {
                  console.error("update failed", e);
                }
              }}
            >
              Mark Available
            </Button>
            <Button variant="ghost" onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button
              leftIcon={<EditIcon />}
              variant="ghost"
              onClick={() =>
                navigate(`/rentals/assets/${asset.asset_id || asset._id}/edit`)
              }
            >
              Edit
            </Button>
            <Button
              variant="ghost"
              onClick={() =>
                navigate(`/rentals/bookings?assetId=${asset.asset_id}`)
              }
            >
              View Bookings
            </Button>
          </HStack>
        </VStack>
      </SimpleGrid>
    </Box>
  );
}
